<script lang="ts" setup>import { toRef as _toRef } from 'vue';

import { useElementSize } from '@vueuse/core'
import postmarkSrc from './postmark.svg'
import EnvelopeBackBody from './EnvelopeBackBody.vue'
import EnvelopeFlap from './EnvelopeFlap.vue'
import EnvelopeFrontArt from './EnvelopeFrontArt.vue'

defineProps<{
  play?: boolean
  fadeOut?: boolean
  open?: boolean
  senderName?: string
  recipientName?: string
}>()

// Measure the envelope's actual rendered size and expose it as CSS
// variables — addressing layout positions and the SVG art are sized
// against these, so they stay in sync with the actual envelope
// dimensions across viewport sizes / resizes.
const envelopeRef = ref<HTMLDivElement>()
const __$temp_1 = (useElementSize(envelopeRef)),
  envelopeHeight = _toRef(__$temp_1, 'height');
watchEffect(() => {
  const el = envelopeRef.value
  if (!el) return
  el.style.setProperty('--env-height', `${envelopeHeight.value}px`)
})
</script>

<template>
  <!--
    Scene wrapper carries the 3D perspective so the flap's rotateX
    flips with depth. The envelope inside has `preserve-3d` so its
    own children (front face, back face, and the flap layered on the
    back) each get their own plane.
  -->
  <div class="scene">
    <div
      ref="envelopeRef"
      class="envelope"
      :class="{ play, 'fade-out': fadeOut, open }"
    >
      <!--
        Inside-back of the envelope: a solid `--env-color` panel
        that sits behind the card and reads as the envelope's
        interior surface. It carries the `.env-face` class so the
        slide-down on fade-out picks it up alongside the front and
        back faces — the entire shell drops as one piece, leaving
        the card alone on stage.
      -->
      <div class="env-face env-interior"/>

      <!--
        Slot contents come after the interior but before the front /
        back faces. In the 3D context the parent establishes, both
        outer faces render AFTER the card in DOM — placing them in
        front of the card. The interior renders BEFORE the card, so
        it sits underneath. Net effect: shell on top, card sandwiched,
        interior just behind the card. The card is only visible
        through openings in the shell (the flap when it rotates up,
        or once the shell fades out on the bottom).
      -->
      <slot/>

      <!--
        BACK face. Becomes visible once the envelope rotates rotateY
        180° (during `.play` / `.open`). Contains:
          1. EnvelopeBackBody — the 3 SVG panels that form the back
             (left / right / bottom triangles converging at center,
             with a natural triangular gap at the top where the flap
             sits — that gap is what the card peeks through after
             the flap opens).
          2. A `.top-flap` layer holding EnvelopeFlap on top — its
             own plane so it can rotate independently around the
             top edge (the open animation).
      -->
      <div class="env-face env-back">
        <EnvelopeBackBody class="env-back-art"/>
        <div class="top-flap">
          <EnvelopeFlap class="env-flap-art"/>
        </div>
      </div>

      <!--
        FRONT face. Airmail SVG behind, addressing overlay on top so
        recipient/sender names and the postmark sit over the artwork.
      -->
      <div class="env-face env-front">
        <EnvelopeFrontArt class="env-front-bg"/>
        <div class="env-front-overlay">
          <div v-if="recipientName?.length" class="address-block to">
            <div class="address-label">To</div>
            <div class="address-name">{{ recipientName }}</div>
          </div>

          <div v-if="senderName?.length" class="address-block from">
            <div class="address-label">From</div>
            <div class="address-name">{{ senderName }}</div>
          </div>

          <div class="postmark">
            <img :src="postmarkSrc" alt="Postmark">
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
// The scene stays at a fixed 320×320 dimension — matches the SVG
// viewBoxes 1:1 so the SVG artwork lands at its native proportions.
// The visual size is controlled by --scene-scale, which the parent
// sets (1 on wide desktop, smaller on mobile).
.scene {
  height: 320px;
  transform: scale(var(--scene-scale, 1));
  transform-origin: center center;
  width: 320px;
}

.envelope {
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  width: 100%;
}

.envelope.play {
  animation: flip-envelope 1.2s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  animation-delay: 0.6s;
}

.env-face {
  backface-visibility: hidden;

  // `color` is the single source the SVG art fills, the interior
  // panel's background, and the slide-down fade keyframe all read
  // from. Putting it here on `.env-face` lets the fade-out animation
  // tween it once and every face dissolves in lockstep.
  // No CSS `box-shadow` here on purpose: shadows on a translating
  // element re-rasterize every frame, which (combined with the
  // concurrent color / fill-opacity / slide animations) starves the
  // compositor on mobile and causes the card's reveal animation to
  // stutter. The SVG art (front airmail, back panels, flap) carries
  // its own drop-shadow via internal filter defs, so the envelope
  // still reads as raised off the page.
  color: var(--env-color, #a83121);
  height: 100%;
  position: absolute;
  width: 100%;
}

// INTERIOR — a flat env-colored panel that lives behind the card.
// `rotateY(180deg)` matches the back face, so `backface-visibility`
// hides it while the envelope is closed and reveals it after the
// flip. It carries `.env-face`, so the slide-down on fade-out picks
// it up with the front and back faces. `background: currentColor`
// (instead of `var(--env-color)`) so the fade-out animation can
// dissolve its background simply by animating `color` to
// `transparent` — same lever the SVG `currentColor` fills use.
.env-interior {
  background: currentcolor;
  transform: rotateY(180deg);
}

// FRONT face — airmail art layered behind the addressing overlay.
// SVG inside reads `currentColor` from `.env-face` above. NB:
// drop-shadow lives on the inner SVG (via the SVG's own filter
// defs), not on this element — applying a CSS `filter` here
// flattens the 3D layer and breaks `preserve-3d` on the envelope,
// which causes the slot content to leak through the front face.
// Box-shadow IS applied here (and only here) to give the closed
// envelope visual depth. `EnvelopeFrontArt` has no internal SVG
// filter (only the back body and flap do), so without this the
// front reads as flat. Safe to apply CSS box-shadow here even
// though it's expensive on translating elements: after the
// rotateY(180°) flip, this face is backface-hidden — the shadow
// stops rendering before the slide-down fade-out kicks in.
.env-front {
  box-shadow: var(--card-envelope-box-shadow);
  transform: rotateY(0deg);
  z-index: 100;
}

.env-front-bg {
  inset: 0;
  position: absolute;
}

// Addressing layout sits on top of the airmail art with a single
// inset matching the SVG's inner frame (the stripe mask leaves the
// inner 13px frame clean).
.env-front-overlay {
  display: grid;
  grid-template: 1fr 1fr / 1fr auto;
  grid-template-areas:
    "to       postmark"
    "from     .";
  inset: 14px;
  padding: 24px 32px;
  position: absolute;
  row-gap: 30px;
}

.address-block {
  align-self: end;
  color: var(--env-text-color);
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  &.to {
    grid-area: to;
  }

  &.from {
    align-self: start;
    grid-area: from;
  }
}

.address-label {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 1.5px;
  opacity: 0.85;
  text-transform: uppercase;
}

.address-name {
  border-bottom: 1px solid rgb(0 0 0 / 35%);
  font-size: 1.375rem;
  font-weight: 700;
  line-height: 1.2;
  overflow: hidden;
  padding-bottom: 6px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.postmark {
  align-items: center;
  align-self: start;
  color: var(--env-text-color);
  opacity: 0.55;
  position: absolute;
  right: 16px;
  top: 16px;
}

// BACK face — the 3-panel SVG body. Hidden until the envelope's
// rotateY 180 brings it forward. The SVG's internal filter defs
// carry the subtle seam shadows between the panels; this CSS
// `box-shadow` adds the outer depth shadow around the whole back
// (which the front face also has via the same `--card-envelope-
// box-shadow` var, so the closed and open envelope read the same).
// It's the only translating face with a CSS shadow during fade-out
// (front is backface-hidden by then, interior has none), so the
// paint cost is one-third of the original three-faces-each setup.
.env-back {
  box-shadow: var(--card-envelope-box-shadow);
  transform: rotateY(180deg);
  transform-style: preserve-3d;
  z-index: 100;
}

.env-back-art {
  inset: 0;
  position: absolute;
}

// Top flap — lives on the back face but on its own plane so it can
// rotate around its top edge (the open animation). `transform-style:
// preserve-3d` lets the SVG inside live in the 3D context so the
// backface tint shows after the flip passes 90°. `color` is
// intentionally NOT set here: the flap inherits from `.env-back`,
// which means the fade-out's `color` animation dissolves the flap
// in lockstep with the rest of the shell.
.top-flap {
  height: 53%; // matches the flap's 170/320 ratio in the SVG viewBox
  left: 0;
  position: absolute;
  top: 0;
  transform: rotateX(0deg) translateZ(1px);
  transform-origin: top center;
  transform-style: preserve-3d;
  width: 100%;
  z-index: 5;
}

.env-flap-art {
  inset: 0;
  position: absolute;
}

// === Animations ===========================================================

// `.play` (one-shot) animations.
.envelope.play .top-flap {
  animation: open-flap 0.7s cubic-bezier(0.6, 0, 0.35, 1.15) forwards;
  animation-delay: 1.9s;
}

// Static "open" state — used when the envelope should render
// already-opened (e.g. as a backdrop in the redemption stage).
// Compatible with `.play`: `forwards` fill keeps the play-animation
// end-state, which matches what `.open` declares here.
.envelope.open {
  transform: rotateY(180deg);
}

.envelope.open .top-flap {
  transform: rotateX(180deg) translateZ(1px);
  z-index: 1;
}

// Backface tint — flipped on past 90° of rotation so the inside of
// the open flap reads a touch darker than the outside. Driven by a
// 0s transition with a delay tied to the open animation's halfway
// point (matching the design reference).
.envelope.open :deep(.env-flap-back-tint),
.envelope.play :deep(.env-flap-back-tint) {
  opacity: 1;
  transition: opacity 0s 2.25s; // mid-flap-animation (1.9s delay + ~0.35s)
}

// FADE-OUT — runs two animations in parallel so the color and the
// slide can have independent timings:
//   • `env-face-color-fade` — dissolves `color` to transparent in
//     0.3s. Because the SVG art reads `currentColor`, the interior
//     panel uses `background: currentcolor`, and `.top-flap`
//     inherits color from `.env-back`, animating the single
//     inherited color fades the whole shell (front airmail, back
//     panels, top flap, interior) by its alpha channel — leaving
//     non-themed children (addressing text, postmark) untouched.
//   • `env-face-slide-down` — slides the now-transparent shell off
//     the bottom in 0.6s.
.envelope.fade-out .env-face {
  animation:
    env-face-color-fade 0.2s ease-out forwards,
    env-face-slide-down 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

// Flap fade — the flap SVG has paths with explicit `fill-opacity`
// attributes (the highlight gradient at 0.2 and the back-tint at
// 0.22) that would NOT be picked up by inherited CSS on a parent
// element. Target each `<path>` directly with `:deep` so each path
// interpolates from its own current `fill-opacity` to 0 — this
// dissolves all three flap layers (main fill, gradient highlight,
// backface tint) in lockstep with the color fade above. Same 0.2s
// duration so they finish together.
.envelope.fade-out .top-flap :deep(path) {
  animation: flap-fill-fade 0.2s ease-out forwards;
}

@keyframes env-face-color-fade {
  0% {
    color: var(--env-color, #a83121);
  }

  100% {
    color: transparent;
  }
}

@keyframes flap-fill-fade {
  to {
    fill-opacity: 0;
  }
}

@keyframes env-face-slide-down {
  0% {
    translate: 0 0;
  }

  100% {
    translate: 0 100dvh;
  }
}

@keyframes flip-envelope {
  0% {
    transform: rotateY(0deg);
  }

  100% {
    transform: rotateY(180deg);
  }
}

@keyframes open-flap {
  0% {
    transform: rotateX(0deg) translateZ(1px);
    z-index: 5;
  }

  // Mid-flip, swap z-index so the once-open flap sits behind the body
  // (like a real flap folded back). Same trick as the design HTML.
  50% {
    z-index: 1;
  }

  100% {
    transform: rotateX(180deg) translateZ(1px);
    z-index: 1;
  }
}
</style>
